<template>
  <div>
    <!-- Policy List Card -->
    <b-card class="templates-list-card">
      <!-- Gradient Line Wrapper -->
      <div class="gradient-line-wrapper"></div>
      <!-- Table Header -->
      <div class="table-header d-flex flex-wrap justify-content-between align-items-center mb-2">
        <div>
          <h5 class="mb-0 template-title">Policy Management</h5>
          <small class="text-muted">Manage and review school policies</small>
        </div>
      </div>

      <!-- Filter Row -->
      <b-row class="align-items-center filter-row-single-line mb-2">
        <!-- Search Input -->
        <b-col xl="3" lg="3" md="4" sm="12" cols="12">
          <b-form-input
            v-model="searchQuery"
            placeholder="Search policies..."
            class="filter-control"
            @input="onSearchChange"
            debounce="500"
          />
        </b-col>
        <!-- Policy Type Filter -->
        <b-col xl="3" lg="3" md="4" sm="12" cols="12">
          <v-select
            v-model="policyType"
            :options="policyTypeOptions"
            placeholder="Policy Type (All)"
            @input="onFilterTypeChange"
            class="filter-select"
            :clearable="true"
          />
        </b-col>
        <!-- Department Filter -->
        <b-col xl="3" lg="3" md="4" sm="12" cols="12" v-if="policyType === 'Staff'">
          <v-select
            v-model="selectedDept"
            :options="departmentOptions"
            :reduce="option => option.id"
            label="name"
            placeholder="Department (All)"
            @input="onFilterChange"
            class="filter-select"
            :clearable="true"
            :loading="departmentLoading"
          />
        </b-col>
        <!-- Action Buttons -->
        <b-col :xl="policyType === 'Staff' ? '3' : '6'" :lg="policyType === 'Staff' ? '3' : '6'" md="7" sm="12" cols="12" class="text-right">
          <div class="d-flex gap-2 align-items-center justify-content-end flex-wrap">
            <b-button
              v-if="rights.add"
              @click="openAddModal"
              variant="gradient-primary"
              class="action-btn-filter"
              :disabled="saving"
            >
              <b-spinner v-if="saving" small class="mr-1" />
              <feather-icon v-else icon="PlusIcon" size="16" class="mr-1" />
              Add Policy
            </b-button>
          </div>
        </b-col>
      </b-row>

      <!-- Pagination Controls - Top -->
      <div class="pagination-controls-top d-flex justify-content-between align-items-center mb-3">
        <div class="d-flex align-items-center gap-2">
            <!-- Page Size Dropdown -->
            <div class="page-size-wrapper">
                <label class="page-size-label">Show:</label>
                <v-select
                    :key="'page-size-' + perPage"
                    v-model="perPage"
                    :options="pageSizeOptions"
                    :reduce="option => option.value"
                    label="label"
                    :clearable="false"
                    :searchable="false"
                    append-to-body
                    class="page-size-select-compact"
                    @input="onPageSizeChange"
                    @option:selected="closePageSizeDropdown"
                    ref="pageSizeSelect"
                />
            </div>
        </div>
        
        <!-- Count Display in Middle -->
        <div class="pagination-count-display">
          <span class="count-text">
            Showing <strong>{{ displayedRange.start }}</strong> to <strong>{{ displayedRange.end }}</strong> of <strong>{{ totalCount }}</strong>
          </span>
        </div>
        
        <!-- Pagination -->
        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            first-number
            last-number
            prev-text="‹"
            next-text="›"
            size="sm"
            align="center"
            class="pagination-compact"
            @change="onPageChange"
          />
        </div>
      </div>

      <!-- Data Grid -->
      <b-table
        class="templates-table"
        :items="policies"
        :fields="fields"
        :busy="tableBusy"
        :responsive="false"
        hover
        show-empty
        :empty-text="tableEmptyText"
      >
        <template #table-busy>
          <tbody>
            <tr v-for="n in 5" :key="'skeleton-' + n" class="skeleton-row">
              <td v-for="(field, index) in fields" :key="'skeleton-cell-' + index" class="skeleton-cell">
                <div class="skeleton-loader"></div>
              </td>
            </tr>
          </tbody>
        </template>

        <template #cell(sno)="{ index }">
          <span class="text-muted">{{ (currentPage - 1) * perPage + index + 1 }}</span>
        </template>

        <template #cell(type)="{ item }">
          <b-badge :variant="getBadgeVariant(item.type)" class="template-for-badge">
            {{ item.type || 'N/A' }}
          </b-badge>
        </template>

        <template #cell(department)="{ item }">
          <b-badge v-if="item.type === 'Staff'" variant="light-info">
            {{ item.departmentName || 'N/A' }}
          </b-badge>
          <span v-else>-</span>
        </template>

        <template #cell(title)="{ item }">
          <span class="template-cell font-weight-bolder">{{ item.title || 'N/A' }}</span>
        </template>


        <template #cell(content)="{ item }">
          <div class="template-cell-truncated">
            <span v-if="getPlainTextFromFormat(item.content).length > 100">
              {{ getPlainTextFromFormat(item.content).substring(0, 100) }}...
            </span>
            <span v-else>{{ getPlainTextFromFormat(item.content) || 'N/A' }}</span>
          </div>
        </template>

        <template #cell(actions)="{ item }">
          <div class="d-flex align-items-center justify-content-end">
            <b-button
              variant="gradient-primary"
              v-b-tooltip.hover.left
              title="View Details"
              size="sm"
              class="action-btn-table mr-1"
              @click="viewPolicy(item)"
              :disabled="editLoading === item.id || deleteLoading === item.id"
            >
              <feather-icon icon="EyeIcon" size="16" />
            </b-button>
            
            <b-button
              v-if="rights.edit"
              variant="outline-success"
              v-b-tooltip.hover.top
              title="Edit"
              size="sm"
              class="action-btn-outline edit-btn mr-1"
              @click="editPolicy(item)"
              :disabled="editLoading === item.id || deleteLoading === item.id"
            >
              <b-spinner v-if="editLoading === item.id" small type="grow" />
              <feather-icon v-else icon="EditIcon" size="16" />
            </b-button>
            
            <b-button
              v-if="rights.delete"
              variant="outline-danger"
              v-b-tooltip.hover.right
              title="Delete"
              size="sm"
              class="action-btn-outline delete-btn"
              @click="deletePolicy(item)"
              :disabled="editLoading === item.id || deleteLoading === item.id"
            >
              <b-spinner v-if="deleteLoading === item.id" small type="grow" />
              <feather-icon v-else icon="Trash2Icon" size="16" />
            </b-button>
          </div>
        </template>
      </b-table>
    </b-card>

    <!-- Add/Edit Modal -->
    <b-modal
      v-model="showModal"
      :title="isEdit ? 'Edit Policy' : 'Add Policy'"
      @hidden="resetForm"
      centered
      size="lg"
      no-close-on-backdrop
      class="template-modal"
    >
      <b-form @submit.prevent>
        <b-row>
            <b-col md="12">
                <b-form-group label="Title" label-for="title">
                <b-form-input
                    id="title"
                    v-model="form.title"
                    placeholder="Enter policy title"
                    :disabled="saving"
                    class="modern-input"
                />
                </b-form-group>
            </b-col>
        </b-row>
        <b-row>
            <b-col md="6">
                <b-form-group label="Policy Type" label-for="type">
                    <v-select
                        id="type"
                        v-model="form.type"
                        :options="policyTypeOptions"
                        placeholder="Select Policy Type"
                        :disabled="saving"
                        class="modern-select"
                        @input="onFormTypeChange"
                    />
                </b-form-group>
            </b-col>
            <b-col md="6" v-if="form.type === 'Staff'">
                <b-form-group label="Department" label-for="department">
                    <v-select
                        id="department"
                        v-model="form.deptID"
                        :options="departmentOptions"
                        :reduce="option => option.id"
                        label="name"
                        placeholder="Select Department"
                        :disabled="saving || departmentLoading"
                        :loading="departmentLoading"
                        class="modern-select"
                    />
                </b-form-group>
            </b-col>
        </b-row>



        <b-form-group label="Content" label-for="content">
          <quill-editor
            id="content"
            ref="quillEditor"
            v-model="form.content"
            :options="{
              modules: {
                toolbar: editorOptions,
              },
              placeholder: 'Enter policy content...',
              theme: 'snow',
            }"
            :disabled="saving"
            class="quill-editor-wrapper"
          />
        </b-form-group>
      </b-form>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showModal = false" :disabled="saving" class="btn-modern-cancel">
          Cancel
        </b-button>
        <b-button variant="primary" @click="savePolicy" :disabled="saving || !isFormValid" class="btn-modern-primary">
          <b-spinner v-if="saving" small class="mr-2" />
          <feather-icon v-else icon="SaveIcon" size="16" class="mr-2" />
          {{ saving ? 'Saving...' : isEdit ? 'Update' : 'Save' }}
        </b-button>
      </template>
    </b-modal>

    <!-- View Modal -->
    <b-modal
      v-model="showViewModal"
      title="View Policy"
      @hidden="resetViewModal"
      centered
      size="lg"
      no-close-on-backdrop
      class="template-modal"
    >
      <div class="view-template-content">
        <b-form-group label="Title">
          <div class="view-field-value">{{ viewData.title || 'N/A' }}</div>
        </b-form-group>

        <b-form-group label="Policy Type">
          <div class="view-field-value">
             <b-badge :variant="getBadgeVariant(viewData.type)">
              {{ viewData.type || 'N/A' }}
            </b-badge>
          </div>
        </b-form-group>

        <b-form-group label="Department" v-if="viewData.type === 'Staff'">
          <div class="view-field-value">
            <b-badge variant="light-info">
              {{ viewData.departmentName || 'N/A' }}
            </b-badge>
          </div>
        </b-form-group>



        <b-form-group label="Content">
          <div class="view-format-content" v-html="viewData.content"></div>
        </b-form-group>
      </div>

      <template #modal-footer>
        <b-button variant="outline-secondary" @click="showViewModal = false" class="btn-modern-cancel">
          Close
        </b-button>
      </template>
    </b-modal>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BButton,
  BTable,
  BPagination,
  BForm,
  BFormGroup,
  BFormInput,
  BSpinner,
  BModal,
  VBTooltip,
  BBadge,
} from 'bootstrap-vue'
import vSelect from 'vue-select'
import { quillEditor } from 'vue-quill-editor'
import { mapActions } from 'vuex'

export default {
  name: 'Policy',
  components: {
    BCard,
    BRow,
    BCol,
    BButton,
    BTable,
    BPagination,
    BForm,
    BFormGroup,
    BFormInput,
    BSpinner,
    BModal,
    vSelect,
    quillEditor,
    BBadge,
  },
  directives: {
    'b-tooltip': VBTooltip,
  },
  data() {
    return {
      rights: {},
      policies: [],
      dataLoading: false,
      saving: false,
      policiesCache: {},
      cacheTTL: 2 * 60 * 1000, 
      currentRequestKey: null,
      editLoading: null,
      deleteLoading: null,
      currentPage: 1,
      perPage: 10,
      totalCount: 0,
      searchQuery: '',
      searchTimeout: null,
      showModal: false,
      showViewModal: false,
      isEdit: false,
      policyType: null,
      selectedDept: null,
      policyTypeOptions: ['Student', 'Staff'],
      departmentOptions: [],
      departmentLoading: false,
      viewData: {
        title: '',
        content: '',
        type: '',
        isActive: true
      },
      pageSizeOptions: [
        { label: '10', value: 10 },
        { label: '20', value: 20 },
        { label: '40', value: 40 },
        { label: '80', value: 80 },
        { label: '100', value: 100 },
        { label: '200', value: 200 }
      ],
      fields: [
        { key: 'sno', label: 'S.No', sortable: false, thStyle: { width: '80px', textAlign: 'center' }, tdClass: 'text-center' },
        { key: 'title', label: 'Title', sortable: false, thStyle: { width: 'auto', minWidth: '200px' } },
        { key: 'type', label: 'Type', sortable: false, thStyle: { width: '150px' } },
        { key: 'department', label: 'Department', sortable: false, thStyle: { width: '150px' } },
        { key: 'content', label: 'Summary', sortable: false, thStyle: { width: 'auto', minWidth: '300px' } },
        { key: 'actions', label: 'Actions', sortable: false, thStyle: { width: '200px', textAlign: 'center' }, tdClass: 'actions-cell text-center' },
      ],
      form: {
        id: 0,
        title: '',
        content: '',
        type: 'Student', 
        deptID: null,
        isActive: true,
      },
      editorOptions: [
        ["bold", "italic", "underline", "strike"],
        ["blockquote"],
        [{ header: 1 }, { header: 2 }],
        [{ list: "ordered" }, { list: "bullet" }],
        [{ script: "sub" }, { script: "super" }],
        [{ indent: "-1" }, { indent: "+1" }],
        [{ direction: "rtl" }],
        [{ size: ["small", false, "large", "huge"] }],
        [{ header: [1, 2, 3, 4, 5, 6, false] }],
        [{ color: [] }, { background: [] }],
        [{ align: [] }],
        ["link", "image"],
      ],
    }
  },
  computed: {
    isFormValid() {
      const titleValid = this.form.title && this.form.title.trim().length > 0
      const typeValid = !!this.form.type
      
      let contentValid = false
      if (this.form.content) {
        const tempDiv = document.createElement('div')
        tempDiv.innerHTML = this.form.content
        const textContent = tempDiv.textContent || tempDiv.innerText || ''
        contentValid = textContent.trim().length > 0
      }
      
      return titleValid && contentValid && typeValid
    },
    tableBusy() {
      return this.dataLoading
    },
    tableEmptyText() {
      return this.tableBusy ? '' : 'No policies found'
    },
    displayedRange() {
      if (this.totalCount === 0) return { start: 0, end: 0 }
      const start = (this.currentPage - 1) * this.perPage + 1
      const end = Math.min(this.currentPage * this.perPage, this.totalCount)
      return { start, end }
    },
  },
  created() {
    // Initialize rights from menu and enforce view permission
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
          assignSections: ch.assignSections,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[this.$route.name];
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    
    this.loadPolicies()
    this.loadDepartments()
  },
  
  mounted() {
    this.handleClickOutside = (event) => {
      const clickedElement = event.target
      const isVSelectClick = clickedElement.closest('.v-select') || 
                            clickedElement.closest('.vs__dropdown-menu') ||
                            clickedElement.closest('.vs__dropdown-option') ||
                            clickedElement.closest('.vs__dropdown-toggle')
      
      if (!isVSelectClick) {
        const vSelectRefs = ['pageSizeSelect']
        vSelectRefs.forEach((refName) => {
          if (this.$refs[refName] && this.$refs[refName].$el) {
            const toggle = this.$refs[refName].$el.querySelector('.vs__dropdown-toggle')
            if (toggle) toggle.blur()
          }
        })
      }
    }
    
    document.addEventListener('click', this.handleClickOutside, true)
  },
  
  beforeDestroy() {
    if (this.handleClickOutside) {
      document.removeEventListener('click', this.handleClickOutside, true)
    }
  },
  methods: {
    ...mapActions({ get: 'get', post: 'post', delete: 'delete' }),
    async loadPolicies() {
      const search = this.searchQuery?.trim() || ''
      const cacheKey = `${this.currentPage}|${this.perPage}|${search}|${this.policyType || ''}|${this.selectedDept || ''}`

      const cached = this.policiesCache[cacheKey]
      if (cached && Date.now() - cached.timestamp < this.cacheTTL) {
        this.policies = cached.data
        this.totalCount = cached.totalCount
        return
      }

      this.dataLoading = true
      this.currentRequestKey = cacheKey
      try {
        const params = new URLSearchParams({
          db: this.$store.state.userData.db,
          cID: this.$store.state.userData.cId,
          page: this.currentPage,
          pageSize: this.perPage,
          type: this.policyType || '',
          deptID: this.selectedDept || ''
        })

        if (search) params.append('search', search)

        const obj = {
          url: this.$store.state.domain + 'Policy/GetByPagination?' + params.toString(),
          token: this.$store.state.userData.token,
          dashboard: true,
        }
        const res = await this.get(obj)

        if (this.currentRequestKey !== cacheKey) return

        if (res && (res.status === 'success' || res.data)) {
          this.policies = res.data || []
          this.totalCount = res.totalCount || res.total || (res.data ? res.data.length : 0)
        } else {
          this.policies = []
          this.totalCount = 0
        }

        this.policiesCache[cacheKey] = {
          data: this.policies,
          totalCount: this.totalCount,
          timestamp: Date.now(),
        }
      } catch (error) {
        this.$bvToast.toast('Failed to load policies', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.dataLoading = false
      }
    },
    onSearchChange() {
      if (this.searchTimeout) clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.currentPage = 1
        this.loadPolicies()
      }, 500)
    },
    onFilterTypeChange() {
      this.selectedDept = null
      this.currentPage = 1
      this.loadPolicies()
    },
    onFilterChange() {
      this.currentPage = 1
      this.loadPolicies()
    },
    onPageChange(page) {
      this.currentPage = page
      this.loadPolicies()
    },
    onPageSizeChange() {
      this.currentPage = 1
      this.loadPolicies()
    },
    closePageSizeDropdown() {
      this.$nextTick(() => {
        if (this.$refs.pageSizeSelect) {
          const toggle = this.$refs.pageSizeSelect.$el?.querySelector('.vs__dropdown-toggle')
          if (toggle) toggle.blur()
        }
      })
    },
    openAddModal() {
      this.isEdit = false
      this.form = {
        id: 0,
        title: '',
        content: '',
        type: 'Student',
        deptID: null,
        isActive: true,
      }
      this.showModal = true
    },
    async loadDepartments() {
      this.departmentLoading = true
      try {
        const obj = {
          url: this.$store.state.domain + 'StaffDepartments?db=' + this.$store.state.userData.db + '&cId=' + this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
          dashboard: true,
        }
        const res = await this.get(obj)
        if (res && res.status === 'success') {
          this.departmentOptions = res.data || []
        }
      } catch (error) {
        console.error('Failed to load departments:', error)
      } finally {
        this.departmentLoading = false
      }
    },
    onFormTypeChange() {
      if (this.form.type !== 'Staff') {
        this.form.deptID = null
      }
    },
    editPolicy(item) {
      this.editLoading = item.id
      this.isEdit = true
      
      this.form = {
        id: item.id,
        title: item.title || '',
        content: item.content || '',
        type: item.type || 'Student',
        deptID: item.deptID || null,
        isActive: item.isActive !== undefined ? item.isActive : true,
      }
      this.showModal = true
      this.$nextTick(() => { this.editLoading = null })
    },
    async savePolicy() {
      if (!this.isFormValid) return

      this.saving = true
      try {
        const payload = {
          ...this.form,
          cID: this.$store.state.userData.cId
        }

        const obj = {
          url: this.$store.state.domain + 'Policy/Save?db=' + this.$store.state.userData.db,
          body: payload,
          token: this.$store.state.userData.token,
          message: this.isEdit ? 'Policy updated successfully' : 'Policy added successfully',
          context: this,
        }

        const success = await this.post(obj)
        if (success) {
          this.policiesCache = {}
          this.showModal = false
          this.loadPolicies()
        }
      } catch (error) {
        this.$bvToast.toast('Failed to save policy', {
          title: 'Error',
          variant: 'danger',
          solid: true,
        })
      } finally {
        this.saving = false
      }
    },
    async deletePolicy(item) {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: 'You won\'t be able to revert this!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!',
      })

      if (result.isConfirmed) {
        this.deleteLoading = item.id
        try {
          const obj = {
            url: this.$store.state.domain + 'Policy/Delete?' + 
                 'db=' + encodeURIComponent(this.$store.state.userData.db) + 
                 '&cID=' + this.$store.state.userData.cId + 
                 '&id=' + item.id,
            token: this.$store.state.userData.token,
            message: 'Policy deleted successfully',
            context: this,
          }

          const success = await this.delete(obj)
          if (success) {
            this.policiesCache = {}
            this.loadPolicies()
          }
        } catch (error) {
           this.$bvToast.toast('Failed to delete policy', {
            title: 'Error',
            variant: 'danger',
            solid: true,
          })
        } finally {
          this.deleteLoading = null
        }
      }
    },
    resetForm() {
      this.isEdit = false
    },
    viewPolicy(item) {
      this.viewData = {
        title: item.title || '',
        content: item.content || '',
        type: item.type || 'N/A',
        departmentName: item.departmentName || '',
        isActive: item.isActive
      }
      this.showViewModal = true
    },
    resetViewModal() {
      this.viewData = {
        title: '',
        content: '',
        type: '',
        isActive: true
      }
    },
    getPlainText(html) {
      if (!html) return ''
      const tempDiv = document.createElement('div')
      tempDiv.innerHTML = html
      return tempDiv.textContent || tempDiv.innerText || ''
    },
    getPlainTextFromFormat(format) {
      if (!format) return ''
      if (format.includes('<') && format.includes('>')) {
        return this.getPlainText(format)
      }
      return format
    },
    getBadgeVariant(type) {
      switch (type?.toLowerCase()) {
        case 'student': return 'light-primary'
        case 'staff': return 'light-success'
        case 'academic': return 'light-info'
        default: return 'light-warning'
      }
    }
  },
}
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";
@import "@core/scss/vue/libs/quill.scss";

/* Keyframe animations */
@keyframes slideUpFade {
  from { opacity: 0; transform: translateY(15px); }
  to { opacity: 1; transform: translateY(0); }
}

.filter-row-single-line {
  gap: 0.5rem;
  flex-wrap: wrap;
  margin-left: -0.25rem;
  margin-right: -0.25rem;
  align-items: center;
  background: transparent !important;
  @media (min-width: 1200px) { flex-wrap: nowrap; }
  > [class*="col-"] {
    padding-left: 0.375rem;
    padding-right: 0.375rem;
    display: flex;
    align-items: center;
  }
}

.filter-control {
  height: 42px !important;
  border-radius: 10px;
  border: 1.5px solid #e0d8ff;
  padding: 0.5rem 0.75rem;
  transition: all 0.2s ease;
  color: #2d1b69;
  font-weight: 500;
  width: 100%;
  &:focus { border-color: #7c5dfa; box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1); outline: none; }
}

.filter-select {
  width: 100%;
  ::v-deep .vs__dropdown-toggle {
    height: 42px !important;
    border-radius: 10px;
    border: 1.5px solid #e0d8ff !important;
    background: #fff !important;
  }
  ::v-deep .vs__selected { color: #2d1b69 !important; font-weight: 500; }
}

.action-btn-filter {
  height: 42px;
  font-weight: 600;
  border-radius: 10px;
  padding: 0.5rem 1rem;
  transition: all 0.3s ease;
  &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15); }
}

.templates-list-card {
  background: linear-gradient(180deg, #ffffff 0%, #faf9ff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 20px;
  padding: 1.5rem;
  box-shadow: 0 10px 30px rgba(115, 103, 240, 0.12);
  margin-bottom: 1.5rem;
  animation: slideUpFade 0.6s ease both;
  position: relative;
  .gradient-line-wrapper {
    position: absolute;
    top: 0; left: 0.75rem; right: 0.75rem; height: 5px;
    background: linear-gradient(90deg, #7c5dfa 0%, #9d7aff 50%, #cbb9ff 100%);
    border-radius: 10px 10px 0 0;
  }
}

.table-header { border-bottom: 2px solid #f0edff; margin-bottom: 1rem; h5 { font-weight: 800; color: #2d1b69; } }

.gap-2 { gap: 0.5rem; }

// Pagination Controls - Top (Compact Single Line)
.pagination-controls-top {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(180deg, #faf9ff 0%, #ffffff 100%);
  border: 1px solid #e0d8ff;
  border-radius: 10px;
  margin-bottom: 1rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: nowrap;
  gap: 1rem;
  position: relative;
  
  @media (max-width: 992px) {
    flex-wrap: wrap;
    gap: 0.75rem;
    
    .pagination-count-display {
      order: 3;
      width: 100%;
      margin: 0.5rem 0 0 0;
      text-align: center;
    }
  }
  
  > .d-flex {
    gap: 0.5rem;
    flex-wrap: nowrap;
    align-items: center;
    flex-shrink: 0;
  }
  
  > div {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }
}

// Page Size Wrapper
.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  white-space: nowrap;
  
  .page-size-label {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
    margin: 0;
    white-space: nowrap;
  }
}

// Compact Page Size Select
.page-size-select-compact {
  min-width: 75px;
  width: 75px;
  
  ::v-deep .vs__dropdown-toggle {
    height: 32px !important;
    min-height: 32px !important;
    border-radius: 6px !important;
    border: 1.5px solid #e0d8ff !important;
    padding: 0.25rem 0.5rem !important;
    background: #fff !important;
    font-size: 0.8125rem !important;
    
    &:hover {
      border-color: #b8a9ff !important;
    }
    
    &:focus-within {
      border-color: #7c5dfa !important;
      box-shadow: 0 0 0 2px rgba(124, 93, 250, 0.1) !important;
    }
  }
  
  ::v-deep .vs__selected {
    margin: 0 !important;
    padding: 0 !important;
    color: #2d1b69 !important;
    font-weight: 600 !important;
    font-size: 0.8125rem !important;
    line-height: 1.5 !important;
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
  }
  
  ::v-deep .vs__selected-options {
    padding: 0 !important;
    display: flex !important;
    align-items: center !important;
  }
  
  ::v-deep .vs__search {
    display: none !important;
    width: 0 !important;
    height: 0 !important;
    margin: 0 !important;
    padding: 0 !important;
  }
  
  ::v-deep .vs__actions {
    padding: 0 0.25rem 0 0.5rem !important;
  }
  
  ::v-deep .vs__dropdown-menu {
    z-index: 99999 !important;
    position: fixed !important;
    min-width: 75px !important;
    width: 75px !important;
    border-radius: 8px !important;
    border: 1.5px solid #e0d8ff !important;
    box-shadow: 0 4px 12px rgba(115, 103, 240, 0.15) !important;
    margin-top: 4px !important;
    background: #ffffff !important;
    overflow: visible !important;
    max-height: 300px !important;
    overflow-y: auto !important;
  }
  
  ::v-deep .vs__dropdown-option {
    padding: 0.5rem 0.75rem !important;
    color: #2d1b69 !important;
    font-weight: 500 !important;
    font-size: 0.8125rem !important;
    background-color: #ffffff !important;
    background-image: none !important;
    opacity: 1 !important;
    visibility: visible !important;
    cursor: pointer !important;
    transition: background-color 0.15s ease !important;
    
    &:hover {
      background-color: #f9fafb !important;
      background-image: none !important;
      color: #1f2937 !important;
    }
    
    &--highlight,
    &--selected {
      background-color: #f3f4f6 !important;
      background-image: none !important;
      color: #1f2937 !important;
      font-weight: 600 !important;
    }
    
    &:active {
      background-color: #e5e7eb !important;
    }
    
    &--selected {
      background-color: #f3f0ff !important;
      color: #7c5dfa !important;
      font-weight: 600 !important;
    }
  }
}

// Pagination Count Display
.pagination-count-display {
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  margin: 0 1rem;
  min-width: 200px;
  
  .count-text {
    font-size: 0.8125rem;
    color: #64748b;
    font-weight: 500;
    white-space: nowrap;
    
    strong {
      color: #2d1b69;
      font-weight: 700;
    }
  }
  
  @media (max-width: 992px) {
    margin: 0;
    min-width: auto;
    width: 100%;
  }
}

// Compact Pagination Wrapper
.pagination-wrapper-compact {
  display: flex;
  align-items: center;
  
  ::v-deep .pagination {
    margin-bottom: 0;
    
    .page-link {
      padding: 0.375rem 0.625rem;
      font-size: 0.8125rem;
      line-height: 1.5;
      border-radius: 6px;
      margin: 0 0.125rem;
      border: 1px solid #e0d8ff;
      color: #64748b;
      font-weight: 500;
      min-width: 32px;
      height: 32px;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &:hover {
        background: #f3f0ff;
        border-color: #b8a9ff;
        color: #7c5dfa;
      }
    }
    
    .page-item.active .page-link {
      background: linear-gradient(135deg, #7c5dfa 0%, #9d7aff 100%);
      border-color: #7c5dfa;
      color: #fff;
      font-weight: 600;
      box-shadow: 0 2px 8px rgba(124, 93, 250, 0.3);
    }
    
    .page-item.disabled .page-link {
      background: #f8fafc;
      border-color: #e0d8ff;
      color: #cbd5e1;
      cursor: not-allowed;
    }
  }
}

.templates-table {
  border-radius: 12px; overflow: hidden; border: 1px solid #e5e7eb;
  ::v-deep th { background: #f8fafc; font-weight: 700; color: #1e293b; font-size: 0.75rem; padding: 0.75rem; border-bottom: 1px solid #e2e8f0; }
  ::v-deep td { vertical-align: middle; padding: 0.75rem; font-size: 0.875rem; }
  ::v-deep tbody tr:hover { background: #f1f5f9; border-left: 2px solid #7c5dfa; }
}

.template-cell-truncated { max-width: 400px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #64748b; }

// Action Buttons - Matching resource.vue
.action-btn-table {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  
  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

// Outline Action Buttons (Edit/Delete) - Matching resource.vue
.action-btn-outline {
  width: 36px;
  height: 36px;
  padding: 0;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-weight: 600;
  background: #ffffff !important;
  
  &.edit-btn {
    border: 2px solid #10b981 !important;
    color: #10b981 !important;
    box-shadow: 0 2px 8px rgba(16, 185, 129, 0.15);
    
    &:hover:not(:disabled) {
      background: #f0fdf4 !important;
      border-color: #059669 !important;
      color: #059669 !important;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(16, 185, 129, 0.25);
    }
  }
  
  &.delete-btn {
    border: 2px solid #ef4444 !important;
    color: #ef4444 !important;
    box-shadow: 0 2px 8px rgba(239, 68, 68, 0.15);
    
    &:hover:not(:disabled) {
      background: #fef2f2 !important;
      border-color: #dc2626 !important;
      color: #dc2626 !important;
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(239, 68, 68, 0.25);
    }
  }
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
}

.view-template-content {
  .view-field-value { padding: 0.75rem 1rem; background: #f8fafc; border-radius: 8px; border: 1px solid #e0d8ff; font-weight: 500; margin-bottom: 1rem; }
  .view-format-content { padding: 1rem; background: #fff; border: 1px solid #e0d8ff; border-radius: 8px; min-height: 200px; max-height: 400px; overflow-y: auto; }
}

::v-deep .modal-content { border-radius: 20px; border: none; box-shadow: 0 20px 60px rgba(0,0,0,0.2); }
::v-deep .modal-header { background: #faf9ff; border-bottom: 2px solid #e0d8ff; border-radius: 20px 20px 0 0; padding: 1.5rem; position: relative;
  &::before { content: ''; position: absolute; top:0; left:0; right:0; height:4px; background: linear-gradient(90deg, #7c5dfa, #9d7aff, #cbb9ff); }
}

.modern-input { border-radius: 10px; border: 1.5px solid #e0d8ff; padding: 0.75rem; &:focus { border-color: #7c5dfa; box-shadow: 0 0 0 3px rgba(124, 93, 250, 0.1); } }

.status-switch-wrapper { background: #f8faff; padding: 0.4rem; border-radius: 12px; border: 1px solid #eef2f7; display: inline-flex; }
.status-switch-btn { border-radius: 8px !important; font-weight: 600; padding: 0.5rem 1.25rem !important; transition: all 0.3s; }

.quill-editor-wrapper {
  ::v-deep .ql-toolbar { border-radius: 10px 10px 0 0; border: 1.5px solid #e0d8ff; background: #fff; }
  ::v-deep .ql-container { border-radius: 0 0 10px 10px; border: 1.5px solid #e0d8ff; min-height: 250px; }
}

.btn-modern-primary {
  border-radius: 10px; font-weight: 600; padding: 0.7rem 2rem;
  background: linear-gradient(135deg, #7c5dfa, #9d7aff); border: none; color: #fff;
  &:hover:not(:disabled) { transform: translateY(-2px); box-shadow: 0 4px 12px rgba(124, 93, 250, 0.4); }
}

.btn-modern-cancel { border-radius: 10px; font-weight: 600; padding: 0.7rem 2rem; border: 1.5px solid #e0d8ff; background: #fff; }

.skeleton-loader { height: 18px; border-radius: 10px; background: linear-gradient(90deg, #f0f0f0 25%, #f8f8f8 50%, #f0f0f0 75%); background-size: 200% 100%; animation: loading 1.5s infinite; }
@keyframes loading { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
</style>
